import { useEffect, useState } from "react";
import { openWhatsApp } from "../lib/whatsapp";
import dayjs from "dayjs";
import { db, eq } from "../lib/api";
import { invalidate, useQuery } from "../lib/hooks";
import { useRefData, type Area } from "../lib/refdata";
import { navigate } from "../lib/router";
import { useSession } from "../lib/session";
import { useLog } from "./QuickLog";
import { BookMeetingButton } from "./Appointments";
import { openPerson } from "./PersonDrawer";
import { Loading, Sheet, useToast } from "./ui";
import { waNumber } from "../pages/Birthdays";

export type DoItem = { item_key: string; kind: string; area: string; title: string; why: string; person_id: string | null; link?: string | null; due?: string | null };
type P = { id: string; full_name: string; first_name: string; preferred_name: string | null; phone: string | null; email: string | null; known_as: string | null; is_client: boolean };

/** Which Command Center items have a "Do it" button. */
export const canDo = (i: DoItem) => !!i.person_id && ["follow_up", "commitment", "review_book", "pipeline", "policy", "birthday", "licensing", "turning_18", "back_burner"].includes(i.kind);

const LEAD_AREAS = ["warm", "networking", "hnw", "travel", "new_connections", "team"];
type Channel = "WhatsApp" | "Text" | "Email" | "Phone call";

/** What to log for each kind of item, by channel. */
function activityFor(kind: string, ch: Channel) {
  const call = ch === "Phone call";
  switch (kind) {
    case "review_book": return "Review times proposed";
    case "birthday": return "Birthday or special-day wish";
    case "turning_18": return "Turning 18 conversation";
    case "commitment": case "policy": case "licensing": return call ? "Check-in call" : "Check-in message";
    default: return call ? "Follow-up call" : "Follow-up message";
  }
}

/** One click from a to-do: the message is written for you, the app opens, and the log is filled in. */
export function DoItSheet({ item, onClose }: { item: DoItem; onClose: () => void }) {
  const ref = useRefData();
  const log = useLog();
  const { profile } = useSession();
  const p = useQuery(`doit:${item.person_id}`, () => db.one<P>("people", { id: eq(item.person_id!), select: "id,full_name,first_name,preferred_name,phone,email,known_as,is_client" }), { staleMs: 0 });
  const person = p.data;
  const first = person ? (person.preferred_name || person.first_name || person.full_name.split(" ")[0]) : "";
  const me = profile?.display_name?.split(" ")[0] ?? "";
  const booking: string | undefined = ref.data?.setting("calendly")?.booking_url ?? ref.data?.setting("calendly")?.scheduling_url;
  const [msg, setMsg] = useState("");
  const [subject, setSubject] = useState("");

  useEffect(() => {
    if (!person) return;
    const child = item.title.split(" ")[0];
    const on = item.why.match(/on ([A-Z][a-z]{2} \d+)/)?.[1];
    const when = /today/.test(item.why) ? "today" : on ? `on ${on}` : "soon";
    const what = item.why.replace(/ · promised .*$/, "").replace(/ · .*book value$/, "");
    const t: Record<string, [string, string]> = {
      review_book: ["Your annual review",
        `Hi ${first}, it's that time of year for your annual review: a quick look at your accounts and coverage to make sure everything still fits your plans.\n\nWould one of these times work for you?\n• \n• \n• ${booking ? `\n\nOr pick a time that suits you here: ${booking}` : ""}\n\n${me}`],
      back_burner: ["Checking in", `Hi ${first}, it's been a little while! How have you been? I'd love to catch up when you have a moment.\n\n${me}`],
      follow_up: ["Following up", `Hi ${first}, just following up${what && what !== "Follow up" ? ` on ${what.charAt(0).toLowerCase() + what.slice(1)}` : ""}. How are things going?\n\n${me}`],
      commitment: ["Quick reminder", `Hi ${first}, a gentle reminder about ${what.replace(/^Committed: /, "").replace(/ · due .*$/, "")}. Let me know if you need anything from me.\n\n${me}`],
      pipeline: ["Following up", `Hi ${first}, following up on our last conversation. Do you have a few minutes this week?\n\n${me}`],
      policy: ["Your policy", `Hi ${first}, one of your policies has a date coming up soon. Can we find 15 minutes to go over your options?\n\n${me}`],
      licensing: ["Your WFG membership", /^Final mark/.test(item.why)
        ? `Hi ${first},\n\nYour extended WFG membership ends on ${item.why.match(/ends ([A-Z][a-z]{2} \d+)/)?.[1] ?? "its final date"}. This is the final 12-month mark: there is no further extension, so you need to be licensed by then to stay active.\n\nWhat's left on your licensing? Let's make a plan this week so you get there in time.\n\n${me}`
        : MEMBERSHIP_MSG.replaceAll("XXXX", first)],
      turning_18: [`${child} is turning 18`, item.item_key === `t18:${person.id}`
        ? `Hi ${first}, you're turning 18 soon, congratulations! It's the perfect time to set up your own financial plan: your TFSA (you can open one from 18), building credit the right way, and protecting your future insurability while you're young and healthy.\n\nCould we find 20 minutes to talk it through?\n\n${me}`
        : `Hi ${first}, ${child} turns 18 ${when}, a big milestone! 🎉\n\nIt's a great time to set up ${child}'s first financial plan: a TFSA (they can open one from 18), what happens with the RESP for school, and their own life insurance while they're young and healthy (it locks in their insurability).\n\nCould we find 20 minutes to talk it through?\n\n${me}`],
      birthday: ["Happy birthday!", ref.data?.setting("birthdays")?.message_adult?.replaceAll("{first_name}", first) ?? `Happy birthday, ${first}! 🎉`],
    };
    const [s, m] = t[item.kind] ?? t.follow_up;
    setSubject(s); setMsg(m);
  }, [person?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const area: Area | undefined = item.kind === "licensing" ? "team" : item.kind === "review_book" || item.kind === "policy" || item.area === "clients" ? "clients"
    : LEAD_AREAS.includes(item.area) ? (item.area as Area) : undefined;

  function go(ch: Channel) {
    if (!person) return;
    const n = waNumber(person.phone);
    try { navigator.clipboard.writeText(msg).catch(() => undefined); } catch { /* not allowed: fine */ }
    if (ch === "WhatsApp" && n) openWhatsApp(person.phone, msg);
    if (ch === "Text" && person.phone) window.location.href = `sms:${person.phone}?&body=${encodeURIComponent(msg)}`;
    if (ch === "Email" && person.email) window.location.href = `mailto:${person.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(msg)}`;
    if (ch === "Phone call" && person.phone) window.location.href = `tel:${person.phone}`;
    if (item.kind === "licensing" && ch !== "Phone call") db.rpc("membership_reminded", { p_member: item.item_key.split(":")[1] }).then(() => invalidate("cc")).catch(() => undefined);
    onClose();
    // then the log, filled in; saving it closes the item
    log({
      person_id: person.id, person_name: person.full_name, area, channel: ch === "Text" ? "Text" : ch,
      activity_type: activityFor(item.kind, ch),
      what_happened: item.kind === "licensing" ? (/^Final mark/.test(item.why) ? "Reminded them of the final 12-month mark: licence needed" : "Reminded them to extend their WFG membership") : item.kind === "turning_18" ? `${item.title} turning 18: financial plan conversation` : ch === "Phone call" ? "" : msg.split("\n")[0].slice(0, 160),
      follow_up_id: item.kind === "follow_up" ? item.item_key.split(":")[1] : undefined,
    });
  }

  return (
    <Sheet title={item.title} onClose={onClose}>
      {!person ? <Loading rows={4} /> : <div className="stack">
        <p className="small muted" style={{ margin: 0 }}>{item.why}{person.known_as ? ` · ${person.known_as}` : ""}</p>
        <>
          <label className="field"><span>Message (edit it, then choose how to send)</span>
            <textarea className="textarea" rows={8} value={msg} onChange={(e) => setMsg(e.target.value)} /></label>
          <div className="doit-ch">
            <button className="btn wa" disabled={!waNumber(person.phone)} onClick={() => go("WhatsApp")}>WhatsApp</button>
            <button className="btn" disabled={!person.email} onClick={() => go("Email")}>Email</button>
            <button className="btn" disabled={!person.phone} onClick={() => go("Text")}>Text</button>
            <button className="btn" disabled={!person.phone} onClick={() => go("Phone call")}>Call</button>
          </div>
          {!person.phone && !person.email && <p className="small" style={{ color: "var(--alert)" }}>No phone or email on file. <button className="linkish" onClick={() => { onClose(); openPerson(person.id); }}>Add them</button></p>}
          <p className="small muted" style={{ margin: 0 }}>The app opens with your message (it's also copied), then the log opens already filled in. Save it and this item clears.</p>
        </>
        <div className="toolbar" style={{ margin: 0 }}>
          {item.kind === "licensing" && !/^Final mark/.test(item.why) && <ExtendedButton memberId={item.item_key.split(":")[1]} mark={item.due} onDone={onClose} />}
          {(item.kind === "review_book" || item.kind === "pipeline" || item.kind === "turning_18") && <BookMeetingButton className="btn small" label="Book the meeting" person={{ id: person.id, full_name: person.full_name }} />}
          <button className="btn small ghost" onClick={() => { onClose(); if (person.is_client && area === "clients") navigate(`/clients/${person.id}`); else openPerson(person.id); }}>Open {first}</button>
          {item.link && item.kind === "pipeline" && <button className="btn small ghost" onClick={() => { onClose(); navigate(item.link!); }}>Open the deal</button>}
        </div>
        <p className="small muted" style={{ margin: 0 }}>{dayjs().format("ddd MMM D")} · logged as {activityFor(item.kind, "WhatsApp")} (or a call), which you can change before saving.</p>
      </div>}
    </Sheet>
  );
}

/** The WFG membership extension message (XXXX = first name). */
export const MEMBERSHIP_MSG = `Hi XXXX,

I just received a notification from WFG that your membership is approaching its expiry deadline.

Since you're still working towards getting licensed, 𝐲𝐨𝐮 𝐧𝐞𝐞𝐝 𝐭𝐨 𝐞𝐱𝐭𝐞𝐧𝐝 𝐲𝐨𝐮𝐫 𝐦𝐞𝐦𝐛𝐞𝐫𝐬𝐡𝐢𝐩 𝐰𝐢𝐭𝐡𝐢𝐧 𝐭𝐡𝐞 𝐧𝐞𝐱𝐭 𝟏𝟓 𝐝𝐚𝐲𝐬 to keep your journey active.

𝐏𝐥𝐞𝐚𝐬𝐞 𝐭𝐚𝐤𝐞 𝐚𝐜𝐭𝐢𝐨𝐧 𝐧𝐨𝐰:

1. Visit https://www.wfglaunch.com/
2. Log in with your 𝐖𝐅𝐆 𝐈𝐃 𝐚𝐧𝐝 𝐩𝐚𝐬𝐬𝐰𝐨𝐫𝐝.
3. Click on your 𝐧𝐚𝐦𝐞/𝐩𝐫𝐨𝐟𝐢𝐥𝐞 and select 𝐄𝐱𝐭𝐞𝐧𝐝 𝐌𝐞𝐦𝐛𝐞𝐫𝐬𝐡𝐢𝐩.
4. Complete the extension process.

⚠️ 𝐈𝐦𝐩𝐨𝐫𝐭𝐚𝐧𝐭: If you cannot find the extension option or experience any issues, let me know immediately so we can raise a support case on your behalf.

𝐏𝐥𝐞𝐚𝐬𝐞 𝐜𝐨𝐦𝐩𝐥𝐞𝐭𝐞 𝐭𝐡𝐢𝐬 𝐭𝐨𝐝𝐚𝐲 𝐚𝐧𝐝 𝐜𝐨𝐧𝐟𝐢𝐫𝐦 𝐨𝐧𝐜𝐞 𝐝𝐨𝐧𝐞.

Let's keep you on track towards getting licensed! 💪`;

/** "They extended it": WFG gives one extension of 3 months on the 9-month mark, which can only be
 *  done from 15 days before the mark until the mark. `mark` is the 9-month mark. */
export function ExtendedButton({ memberId, mark, extended, onDone, label = "They extended ✓" }: { memberId: string; mark?: string | null; extended?: boolean; onDone?: () => void; label?: string }) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState("");
  if (extended) return null;
  const today = dayjs().format("YYYY-MM-DD");
  const opens = mark ? dayjs(mark).subtract(15, "day").format("YYYY-MM-DD") : null;
  if (mark && today > mark) return null;
  if (opens && today < opens) return <span className="small muted" title="WFG only allows the extension in the last 15 days before the 9-month mark">Extension opens {dayjs(opens).format("MMM D")}</span>;
  const until = mark ? dayjs(mark).add(3, "month") : null;
  async function save() {
    setErr("");
    try { await db.rpc("membership_extended", { p_member: memberId }); invalidate("cc"); invalidate("team"); toast("Membership extension recorded"); setOpen(false); onDone?.(); }
    catch (e) { setErr((e as Error).message); }
  }
  if (!open) return <button className="btn small" onClick={() => setOpen(true)}>{label}</button>;
  return (
    <span className="toolbar" style={{ margin: 0 }}>
      <span className="small">Expiry moves to <b>{until ? until.format("MMM D, YYYY") : "the 9-month mark + 3 months"}</b> (9-month mark + 3 months).</span>
      <button className="btn small primary" onClick={save}>Confirm</button>
      <button className="btn small ghost" onClick={() => setOpen(false)}>Cancel</button>
      {err && <span className="small" style={{ color: "var(--alert)" }}>{err}</span>}
    </span>
  );
}
